import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import { BillboardService } from 'services';
import {
  DsRadioToggle,
  DsInputCheckbox,
  DsButton,
  DsIcon,
  DsDialog,
  DsForm,
} from '@tokenyico/design-system/dist/reactJS';
import { BigNumber } from 'bignumber.js';
import { datetimeFormat, numberFormat } from '@tokenyico/design-system';
import { formActions } from 'app/state/actions';
import { formatExchangeRate } from 'app/helpers/currency';
import BillboardCreateOfferView from './BillboardCreateOfferView';
import BillboardOfferView from './BillboardOfferView';
import BillboardOfferStatus from './BillboardOfferStatus';
import TableWrapper from 'app/components/TableWrapper/TableWrapper';

import './styles.less';

const BillboardView = ({ setError, selectedToken }) => {
  const { t } = useTranslation();
  const createDialogRef = useRef();
  const detailDialogRef = useRef();

  const [selectedOffer, setSelectedOffer] = useState();
  const [isOfferOpen, setOfferOpen] = useState(false);
  const [isCreateOfferOpen, setIsCreateOfferOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [tableState, setTableState] = useState({});
  const [tableData, setTableData] = useState([]);
  const [tableTotalRows, setTableTotalRows] = useState(0);
  const [buyOrSell, setBuyOrSell] = useState('buy');
  const [isMyOffers, setIsMyOffers] = useState(false);
  const [buyCounter, setBuyCounter] = useState('0');
  const [sellCounter, setSellCounter] = useState('0');
  const [isDisplayed, setIsDisplayed] = useState('buy');
  const initialHeader = [
    {
      key: 'viewDetails',
      label: '',
      isSortable: false,
    },
    {
      key: 'SELL_TOKEN',
      label: buyOrSell === 'buy' ? t('shared.iWillPay') : t('shared.iWillSell'),
      isSortable: buyOrSell === 'buy',
    },
    {
      key: 'transferIcon',
      label: '',
      isSortable: false,
    },
    {
      key: 'BUY_TOKEN',
      label: buyOrSell === 'buy' ? t('shared.iWillBuy') : t('shared.iWillReceive'),
      isSortable: buyOrSell === 'sell',
    },
    {
      key: 'PRICE',
      label: t('shared.price'),
      isSortable: false,
    },
    {
      key: 'CREATED_AT',
      label: t('shared.createdAt'),
    },
    {
      key: 'EXPIRATION',
      label: t('shared.expiration'),
    },
  ];
  const [header, setHeader] = useState(initialHeader);

  const handleShowCreateOffer = async () => {
    setIsCreateOfferOpen(true);
    createDialogRef.current.open();
  };

  const handleShowOffer = async (e, row) => {
    setSelectedOffer(row);
    setOfferOpen(true);
    detailDialogRef.current.open();
  };

  const toggleLabels = [
    t('shared.buy') + ' (' + buyCounter + ')',
    t('shared.sell') + ' (' + sellCounter + ')',
  ];

  const toggleValues = ['buy', 'sell'];

  const loadData = async (detail) => {
    setIsLoading(true);
    setTableState(detail);

    const query = {
      limit: detail.rowsPerPage,
      offset: (detail.currentPage - 1) * detail.rowsPerPage,
      filter: detail.filter,
      sortBy: detail.sortBy,
      sortDirection: detail.sortDirection,
    };

    try {
      let data;
      if (isMyOffers) {
        const myOffersQuery = {
          ...query,
          [buyOrSell === 'buy' ? 'buyingTokenId' : 'sellingTokenId']: selectedToken.id,
        };
        data = await BillboardService.getOffersMe(selectedToken.id, myOffersQuery);
        setTableTotalRows(data.totalItemsFound);
        const newHeader = [
          ...initialHeader.slice(0, 1),
          {
            key: 'ORDER_STATUS',
            label: t('shared.status'),
          },
          ...initialHeader.slice(1),
        ];
        setHeader(newHeader);
      } else {
        const newQuery = {
          ...query,
          [buyOrSell === 'buy' ? 'sellingTokenId' : 'buyingTokenId']: selectedToken.id,
          status: 'ACTIVE',
        };
        data = await BillboardService.getOffers(selectedToken.id, newQuery);
        setTableTotalRows(data.totalItemsFound);
        setHeader(initialHeader);
      }
      setTableData(data.items);
      setIsDisplayed(buyOrSell);
    } catch (e) {
      setError(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
    } else {
      loadData(tableState);
    }
    setCounters(selectedToken.id);
  }, [selectedToken.id, isMyOffers, buyOrSell]);

  const setCounters = async (tokenId, query) => {
    const buyingTokenQuery = {
      ...query,
      buyingTokenId: tokenId,
      [!isMyOffers ? 'status' : '']: !isMyOffers ? 'ACTIVE' : '',
    };
    const sellingTokenQuery = {
      ...query,
      sellingTokenId: tokenId,
      [!isMyOffers ? 'status' : '']: !isMyOffers ? 'ACTIVE' : '',
    };

    if (isMyOffers) {
      const buyingTokenCount = await BillboardService.getOffersMe(tokenId, buyingTokenQuery);
      setBuyCounter(buyingTokenCount.totalItemsFound);
      const sellingTokenCount = await BillboardService.getOffersMe(tokenId, sellingTokenQuery);
      setSellCounter(sellingTokenCount.totalItemsFound);
    } else {
      const buyingTokenCount = await BillboardService.getOffers(tokenId, buyingTokenQuery);
      setSellCounter(buyingTokenCount.totalItemsFound);
      const sellingTokenCount = await BillboardService.getOffers(tokenId, sellingTokenQuery);
      setBuyCounter(sellingTokenCount.totalItemsFound);
    }
  };

  const resetTable = () => {
    loadData(tableState);
  };

  return (
    <div id="billboard">
      <div className="billboard-nav">
        <DsForm className="toggles">
          <DsRadioToggle
            labels={toggleLabels}
            name="buyOrSell"
            values={toggleValues}
            value={buyOrSell}
            onDsInput={({ detail }) => setBuyOrSell(detail)}
          />
          <DsInputCheckbox
            isToggle
            label={t('shared.showMyOffers')}
            name="myOffers"
            checkedValue="myOffers"
            value={isMyOffers}
            onDsInput={({ detail }) => setIsMyOffers(detail)}
          />
        </DsForm>
        <div className="create-offer">
          <DsButton
            version="primary"
            label={t('shared.createOffer')}
            icon="plus"
            isReverse
            onClick={handleShowCreateOffer}
          />
        </div>
      </div>
      <TableWrapper
        height="100%"
        width="100%"
        header={header}
        data={tableData}
        totalRows={tableTotalRows}
        isSortable
        rowsPerPage={10}
        sortDirection="DESC"
        sortBy="CREATED_AT"
        onFetchData={loadData}
        isLoading={isLoading}
        noData={() => (
          <div className="empty-state">
            <span className="text-secondary _text-md">{t('shared.noOffers')}</span>
          </div>
        )}
        viewDetails={({ row }) =>
          isMyOffers ? (
            <DsButton
              version="monochrome"
              iconSize="1rem"
              isMini
              onClick={(e) => handleShowOffer(e, row)}
              class="_p(0)"
              label={t('shared.details')}
            />
          ) : isDisplayed === 'buy' ? (
            <DsButton
              version="success"
              iconSize="1rem"
              isMini
              onClick={(e) => handleShowOffer(e, row)}
              class="_p(0)"
              label={t('shared.buy') + selectedToken.symbol}
            />
          ) : (
            <DsButton
              version="danger"
              iconSize="1rem"
              isMini
              onClick={(e) => handleShowOffer(e, row)}
              class="_p(0)"
              label={t('shared.sell') + selectedToken.symbol}
            />
          )
        }
        ORDER_STATUS={({ row }) => <BillboardOfferStatus status={row.status} />}
        BUY_TOKEN={({ row }) => (
          <div className="_flex" style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
            {isMyOffers ? (
              <>
                <div>
                  <img
                    className="_m:right(3) _m:top(1)"
                    width="22px"
                    src={row.buyingToken.logo}
                    alt={row.buyingToken.symbol}
                  />
                </div>
                <div className="_flex-column">
                  <span>
                    <b>{numberFormat(row.buyingToken.amount, row.buyingToken.decimals)}</b>{' '}
                    <b>{row.buyingToken.symbol}</b>
                  </span>
                  <span>{row.buyingToken.tokenName}</span>
                </div>
              </>
            ) : (
              <>
                <div>
                  <img
                    className="_m:right(3) _m:top(1)"
                    width="22px"
                    src={row.sellingToken.logo}
                    alt={row.sellingToken.symbol}
                  />
                </div>
                <div className="_flex-column">
                  <span>
                    <b>{numberFormat(row.sellingToken.amount, row.sellingToken.decimals)}</b>{' '}
                    <b>{row.sellingToken.symbol}</b>
                  </span>
                  <span>{row.sellingToken.tokenName}</span>
                </div>
              </>
            )}
          </div>
        )}
        transferIcon={() => (
          <div className="_text-grey-800">
            <DsIcon name="transfer" size="22px" />
          </div>
        )}
        SELL_TOKEN={({ row }) => (
          <div className="_flex" style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
            {isMyOffers ? (
              <>
                <div>
                  <img
                    className="_m:right(3) _m:top(1)"
                    width="22px"
                    src={row.sellingToken.logo}
                    alt={row.sellingToken.symbol}
                  />
                </div>
                <div className="_flex-column">
                  <span>
                    <b>{numberFormat(row.sellingToken.amount, row.sellingToken.decimals)}</b>{' '}
                    <b>{row.sellingToken.symbol}</b>
                  </span>
                  <span>{row.sellingToken.tokenName}</span>
                </div>
              </>
            ) : (
              <>
                <div>
                  <img
                    className="_m:right(3) _m:top(1)"
                    width="22px"
                    src={row.buyingToken.logo}
                    alt={row.buyingToken.symbol}
                  />
                </div>
                <div className="_flex-column">
                  <span>
                    <b>{numberFormat(row.buyingToken.amount, row.buyingToken.decimals)}</b>{' '}
                    <b>{row.buyingToken.symbol}</b>
                  </span>
                  <span>{row.buyingToken.tokenName}</span>
                </div>
              </>
            )}
          </div>
        )}
        PRICE={({ row }) => (
          <>
            {row.buyingToken.tokenType === 'STABLE_COIN'
              ? formatExchangeRate(
                  numberFormat(
                    new BigNumber(row.buyingToken.amount).dividedBy(row.sellingToken.amount),
                    3,
                  ),
                  row.sellingToken.symbol,
                  row.buyingToken.symbol,
                )
              : formatExchangeRate(
                  numberFormat(
                    new BigNumber(row.sellingToken.amount).dividedBy(row.buyingToken.amount),
                    3,
                  ),
                  row.buyingToken.symbol,
                  row.sellingToken.symbol,
                )}
          </>
        )}
        CREATED_AT={({ row }) => datetimeFormat(row.createdAt)}
        EXPIRATION={({ row }) => datetimeFormat(row.expiryDate)}
      />
      <DsDialog
        ref={createDialogRef}
        isModal={true}
        isDismissible={true}
        onClosed={() => setIsCreateOfferOpen(false)}
        id="createOfferDialog"
      >
        <div>
          {isCreateOfferOpen && (
            <BillboardCreateOfferView
              onCloseModal={() => {
                createDialogRef.current.close();
                resetTable();
              }}
            />
          )}
        </div>
      </DsDialog>

      <DsDialog
        ref={detailDialogRef}
        isModal={true}
        isDismissible={true}
        onClosed={() => setOfferOpen(false)}
        id="offerDialog"
      >
        <div>
          {isOfferOpen && (
            <BillboardOfferView
              offer={selectedOffer}
              isCreatedByMe={isMyOffers}
              buyLabel={initialHeader[1].label}
              sellLabel={initialHeader[3].label}
              onCloseModal={() => {
                detailDialogRef.current.close();
                resetTable();
              }}
            />
          )}
        </div>
      </DsDialog>
    </div>
  );
};

const mapToProps = ({ holder: { user, selectedToken } }) => ({ user, selectedToken });

export default connect(mapToProps, formActions)(BillboardView);
